import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  User, Save, Camera, Briefcase, GraduationCap,
  Heart, Shield, Phone, Building, Tag, Award,
  CheckCircle, AlertCircle
} from 'lucide-react';
import { LoadingSpinner } from '../UI/Card';
import type { AcademicYear, VerificationType } from '../../types/database';

export function Profile() {
  const { profile, profileLoading, profileError, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'identity' | 'education' | 'professional' | 'personal' | 'verification'>('identity');
  const [skillInput, setSkillInput] = useState('');
  const [interestInput, setInterestInput] = useState('');

  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    avatar_url: profile?.avatar_url || '',
    bio: profile?.bio || '',
    organization: profile?.organization || '',
    id_number: profile?.id_number || '',
    university: profile?.university || '',
    major: profile?.major || '',
    year: profile?.year || '',
    company: profile?.company || '',
    profession: profile?.profession || '',
    skills: profile?.skills || [],
    interests: profile?.interests || [],
    verification_type: profile?.verification_type || '',
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        avatar_url: profile.avatar_url || '',
        bio: profile.bio || '',
        organization: profile.organization || '',
        id_number: profile.id_number || '',
        university: profile.university || '',
        major: profile.major || '',
        year: profile.year || '',
        company: profile.company || '',
        profession: profile.profession || '',
        skills: profile.skills || [],
        interests: profile.interests || [],
        verification_type: profile.verification_type || '',
      });
    }
  }, [profile]);

  const calculateProfileCompletion = (): number => {
    if (!profile) return 0;

    const fields = [
      profile.full_name,
      profile.email,
      profile.phone,
      profile.avatar_url,
      profile.bio,
      profile.skills?.length > 0,
      profile.interests?.length > 0,
    ];

    if (profile.user_type === 'student') {
      fields.push(profile.university, profile.major, profile.year);
    } else if (profile.user_type === 'professional') {
      fields.push(profile.company, profile.profession);
    }

    const filledFields = fields.filter(Boolean).length;
    return Math.round((filledFields / fields.length) * 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const completion = calculateProfileCompletion();

      const { error } = await supabase
        .from('profiles')
        .update({
          ...formData,
          profile_completion: completion,
          updated_at: new Date().toISOString(),
        })
        .eq('id', profile!.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      await refreshProfile();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };

  const removeSkill = (skill: string) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });
  };

  const addInterest = () => {
    if (interestInput.trim() && !formData.interests.includes(interestInput.trim())) {
      setFormData({ ...formData, interests: [...formData.interests, interestInput.trim()] });
      setInterestInput('');
    }
  };

  const removeInterest = (interest: string) => {
    setFormData({ ...formData, interests: formData.interests.filter(i => i !== interest) });
  };

  const tabs = [
    { id: 'identity' as const, label: 'Identity', icon: User },
    { id: 'education' as const, label: 'Education', icon: GraduationCap, show: profile?.user_type === 'student' },
    { id: 'professional' as const, label: 'Professional', icon: Briefcase, show: profile?.user_type === 'professional' },
    { id: 'personal' as const, label: 'Personal', icon: Heart },
    { id: 'verification' as const, label: 'Verification', icon: Shield },
  ].filter(tab => tab.show !== false);

  if (profileLoading) {
    return <LoadingSpinner />;
  }

  if (profileError || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass-card max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Unable to Load Profile</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {profileError || 'Profile not found. Please try again.'}
          </p>
          <button
            onClick={refreshProfile}
            className="glass-button w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-blue-600 transition-all duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const profileCompletion = calculateProfileCompletion();

  return (
    <div className="min-h-screen">
      <div className="glass-card bg-gradient-to-r from-indigo-600 to-blue-500 text-white py-12 mx-4 mt-4 mb-6 shadow-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                {formData.avatar_url ? (
                  <img
                    src={formData.avatar_url}
                    alt={formData.full_name}
                    className="w-20 h-20 rounded-full border-4 border-white object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full border-4 border-white bg-white/20 flex items-center justify-center">
                    <User className="w-10 h-10" />
                  </div>
                )}
                {profile.id_verified && (
                  <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1 border-2 border-white">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold">{profile.full_name}</h1>
                <p className="text-indigo-100 capitalize">{profile.user_type}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-indigo-100 mb-1">Profile Completion</div>
              <div className="flex items-center space-x-2">
                <div className="w-32 h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white transition-all duration-300"
                    style={{ width: `${profileCompletion}%` }}
                  />
                </div>
                <span className="text-lg font-bold">{profileCompletion}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message && (
          <div
            className={`glass-card mb-6 p-4 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="glass-card mb-6">
          <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors duration-300 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="glass-card p-8">
            {activeTab === 'identity' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Identity Information</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      id="full_name"
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="glass-input w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      id="email"
                      type="email"
                      disabled
                      value={formData.email}
                      className="glass-input w-full opacity-50 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="glass-input w-full pl-10"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="id_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      ID Number
                    </label>
                    <input
                      id="id_number"
                      type="text"
                      value={formData.id_number}
                      onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                      className="glass-input w-full"
                      placeholder="Student ID or Government ID"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="avatar_url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <div className="flex items-center space-x-2">
                      <Camera className="w-4 h-4" />
                      <span>Avatar URL</span>
                    </div>
                  </label>
                  <input
                    id="avatar_url"
                    type="url"
                    value={formData.avatar_url}
                    onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                    className="glass-input w-full"
                    placeholder="https://example.com/avatar.jpg"
                  />
                  {formData.avatar_url && (
                    <div className="mt-3">
                      <img
                        src={formData.avatar_url}
                        alt="Avatar preview"
                        className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Organization
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="organization"
                      type="text"
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      className="glass-input w-full pl-10"
                      placeholder="Company, School, or Community"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'education' && profile.user_type === 'student' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Education Details</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="university" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      University / College
                    </label>
                    <input
                      id="university"
                      type="text"
                      value={formData.university}
                      onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                      className="glass-input w-full"
                      placeholder="e.g., Stanford University"
                    />
                  </div>

                  <div>
                    <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Academic Year
                    </label>
                    <select
                      id="year"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value as AcademicYear })}
                      className="glass-input w-full"
                    >
                      <option value="">Select Year</option>
                      <option value="Freshman">Freshman</option>
                      <option value="Sophomore">Sophomore</option>
                      <option value="Junior">Junior</option>
                      <option value="Senior">Senior</option>
                      <option value="Graduate">Graduate</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="major" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Major / Field of Study
                  </label>
                  <input
                    id="major"
                    type="text"
                    value={formData.major}
                    onChange={(e) => setFormData({ ...formData, major: e.target.value })}
                    className="glass-input w-full"
                    placeholder="e.g., Computer Science"
                  />
                </div>
              </div>
            )}

            {activeTab === 'professional' && profile.user_type === 'professional' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Professional Information</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Company
                    </label>
                    <input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="glass-input w-full"
                      placeholder="e.g., Tech Corp"
                    />
                  </div>

                  <div>
                    <label htmlFor="profession" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Profession / Job Title
                    </label>
                    <input
                      id="profession"
                      type="text"
                      value={formData.profession}
                      onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                      className="glass-input w-full"
                      placeholder="e.g., Software Engineer"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'personal' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Personal Details</h2>

                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bio
                  </label>
                  <textarea
                    id="bio"
                    rows={4}
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Tell us about yourself..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <div className="flex items-center space-x-2">
                      <Tag className="w-4 h-4" />
                      <span>Skills</span>
                    </div>
                  </label>
                  <div className="flex space-x-2 mb-3">
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                      className="glass-input flex-1"
                      placeholder="Add a skill and press Enter"
                    />
                    <button
                      type="button"
                      onClick={addSkill}
                      className="glass-button px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg hover:from-indigo-700 hover:to-blue-600 transition-all duration-300"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center px-3 py-1 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-400 rounded-full text-sm"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill(skill)}
                          className="ml-2 text-indigo-500 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <div className="flex items-center space-x-2">
                      <Heart className="w-4 h-4" />
                      <span>Interests</span>
                    </div>
                  </label>
                  <div className="flex space-x-2 mb-3">
                    <input
                      type="text"
                      value={interestInput}
                      onChange={(e) => setInterestInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                      className="glass-input flex-1"
                      placeholder="Add an interest and press Enter"
                    />
                    <button
                      type="button"
                      onClick={addInterest}
                      className="glass-button px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg hover:from-indigo-700 hover:to-blue-600 transition-all duration-300"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.interests.map((interest) => (
                      <span
                        key={interest}
                        className="inline-flex items-center px-3 py-1 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 rounded-full text-sm"
                      >
                        {interest}
                        <button
                          type="button"
                          onClick={() => removeInterest(interest)}
                          className="ml-2 text-green-500 hover:text-green-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'verification' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Verification Status</h2>

                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-full ${
                      profile.id_verified
                        ? 'bg-green-100'
                        : profile.verification_status === 'pending'
                        ? 'bg-yellow-100'
                        : 'bg-gray-100'
                    }`}>
                      {profile.id_verified ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : profile.verification_status === 'pending' ? (
                        <AlertCircle className="w-6 h-6 text-yellow-600" />
                      ) : (
                        <Shield className="w-6 h-6 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-1">
                        {profile.id_verified ? 'Verified Account' : 'Account Verification'}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-3">
                        {profile.id_verified
                          ? 'Your account has been successfully verified.'
                          : profile.verification_status === 'pending'
                          ? 'Your verification request is being reviewed.'
                          : 'Verify your account to access exclusive perks and features.'}
                      </p>
                      {profile.verification_type && (
                        <div className="inline-flex items-center px-3 py-1 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-400 rounded-full text-sm">
                          <Award className="w-4 h-4 mr-1" />
                          {profile.verification_type}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {!profile.id_verified && (
                  <div>
                    <label htmlFor="verification_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Verification Type
                    </label>
                    <select
                      id="verification_type"
                      value={formData.verification_type}
                      onChange={(e) => setFormData({ ...formData, verification_type: e.target.value as VerificationType })}
                      className="glass-input w-full"
                    >
                      <option value="">Select verification type</option>
                      <option value="Student ID">Student ID</option>
                      <option value="Government ID">Government ID</option>
                      <option value="Community Verified">Community Verified</option>
                    </select>
                  </div>
                )}

                <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                  <h4 className="font-semibold text-indigo-900 dark:text-indigo-300 mb-2">Verification Benefits</h4>
                  <ul className="space-y-2 text-sm text-indigo-800 dark:text-indigo-300">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Access to exclusive verified-only discounts</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Priority access to events and opportunities</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Verified badge on your profile and digital ID</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Enhanced trust within the community</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-6 border-t border-gray-200 mt-8">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="glass-button px-6 py-3 rounded-lg transition-all duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="glass-button flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-blue-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
